import {useEffect, useMemo} from "react";
import PeriodScaleLegends from "./period-scale-legends.jsx";
import ReportUtils from "../../../utils/report-utils.js";

const defaultScaleColors = {
    light: "scale-light",
    mediumLight: "scale-mediumLight",
    mediumDark: "scale-mediumDark",
    dark: "scale-dark",
}

const defaultPeriodColors = {
    light: "period-light",
    mediumLight: "period-mediumLight",
    mediumDark: "period-mediumDark",
    dark: "period-dark",
}

const columns = [
    { key: "segunda", label: "Segunda" },
    { key: "terca", label: "Terça" },
    { key: "quarta", label: "Quarta" },
    { key: "quinta", label: "Quinta" },
    { key: "sexta", label: "Sexta" },
    { key: "sabado", label: "Sábado" },
    { key: "domingo", label: "Domingo" },
]

export default function ReportHeatmapTable({ data = [], title = "", tableId = "heatmap-data", sheetName, rowLabelKey = "hour", rowPeriodKey = "t", minKey = "min", maxKey = "max", showLegend = true, showExportableTable = true, emptyValue = 0, className = "", divisorClassName = '' }) {
    useEffect(() => {
        if (!showExportableTable || !data.length) return;

        ReportUtils.createExportableTable(null, null, sheetName || title, tableId);
    }, [data, showExportableTable, sheetName, title, tableId]);

    const { scale, hasData, scaleColors, periodColors } = useMemo(() => {
        if (!data.length) {
            return {
                hasData: false,
                scale: {
                    first: 0,
                    firstMid: 0,
                    mid: 0,
                    midLast: 0,
                    last: 0,
                },
                scaleColors: defaultScaleColors,
                periodColors: defaultPeriodColors,
            };
        }

        const first = Number(data[0]?.[minKey] ?? 0);
        const last = Number(data[0]?.[maxKey] ?? 0);
        const mid = Math.floor((first + last) / 2);
        const firstMid = Math.floor((first + mid) / 2);
        const midLast = Math.floor((mid + last) / 2);

        return {
            hasData: true,
            scale: {
                first,
                firstMid,
                mid,
                midLast,
                last,
            },
            scaleColors: defaultScaleColors,
            periodColors: defaultPeriodColors,
        };
    }, [data, minKey, maxKey]);

    const getCellBgColor = (value) => {
        const numericValue = Number(value ?? 0);

        if (numericValue >= scale.midLast && numericValue <= scale.last) {
            return scaleColors.dark;
        }

        if (numericValue >= scale.mid && numericValue <= scale.midLast) {
            return scaleColors.mediumDark;
        }

        if (numericValue >= scale.firstMid && numericValue <= scale.mid) {
            return scaleColors.mediumLight;
        }

        if (numericValue >= scale.first && numericValue <= scale.firstMid) {
            return scaleColors.light;
        }

        return "scale-none";
    }

    const getPeriodBgColor = (t) => {
        const hour = Number(t ?? 0);

        if (hour < 6) return periodColors.light;
        if (hour >= 6 && hour < 12) return periodColors.mediumLight;
        if (hour >= 12 && hour < 18) return periodColors.mediumDark;
        if (hour >= 18 && hour < 25) return periodColors.dark;

        return "";
    }

    if (!hasData) return null;

    return (
        <>
            <div className={`print:break-before-page print:break-inside-avoid ${className}`}>
                <span className="chart-title">{title}</span>
                <table id={tableId} className={"table report-table mt-2"} sheet-name={sheetName || title}>
                    <thead className="thead-background">
                    <tr className="bg-[#f6f6f6] text-left">
                        <th className={`${periodColors.light} text-center`}>#</th>
                        {columns.map((column) => (
                          <th key={column.key} className={periodColors.light}>
                              {column.label}
                          </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody className="bg-white">
                    {data.map((row, index) => (
                      <tr key={index}>
                          <td className={getPeriodBgColor(row[rowPeriodKey])}>
                              {row[rowLabelKey] ?? ""}
                          </td>
                          {columns.map((column) => (
                            <td key={column.key} className={getCellBgColor(row[column.key])}>
                                {row[column.key] ?? emptyValue}
                            </td>
                          ))}
                      </tr>
                    ))}
                    </tbody>
                </table>

                {showLegend && (
                  <PeriodScaleLegends
                    first={scale.first}
                    firstMid={scale.firstMid}
                    mid={scale.mid}
                    midLast={scale.midLast}
                    last={scale.last}
                  />
                )}
            </div>

            <div id="chart-tables"/>
            <div className={`chart-divisor ${divisorClassName}`}/>
        </>
    )
}